import h from "virtual-dom/h";
import * as CaptureTools from "../utilities/capture-tools";
import * as Actions from "../actions/actions";
import CalendarWidget from "../elements/calendarWidget";
import { Store } from "redux";
import { DefaultSettings } from "../../../../settings/settings-util";
import { AlmeState } from "../../../../wireup/AlmeState";
import { Dictionary, Mutator } from "../../../../types/rendering";
import { VChild, VNode } from "virtual-dom";
import { DataCollectionPaneConfig } from "../models/DataCollectionPaneConfig";
import { CalendarWidgetConfig, MetaData, SaveDateTimeFun } from "../models/CalendarWidgetConfig";


type Renderers = (config: DataCollectionPaneConfig) => VChild;


export default class Renderer
{
    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _mutators: Mutator[];
    _childRenderers: Renderers[];
    _calendarWidget: CalendarWidget;

    //#region Public Interface
    constructor(settings: DefaultSettings, mutators: Mutator[], childRenderers: Renderers[])
    {
        this._settings = settings;
        this._mutators = mutators;
        this._childRenderers = childRenderers;
        this._calendarWidget = new CalendarWidget(settings);
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;
    }

    getRender()
    {
        return (message: CalendarWidgetConfig): VNode | undefined =>
        {
            if (!this.doesRender(message)) return;

            let parameters: Dictionary =
            {
                className : this._settings.classNames.calendarPane,
            };

            parameters = this._mutators.reduce((params, mutate) => mutate(params), parameters);

            let childNodes = this._childRenderers.map(render => render(message));

            const { closeIcon,
                  header,
                  prompt,
                  paneId,
                  active,
                  metadata } = message;

            const capturedLocation = this._settings.stateLocations.History.capturedFeedback;
            const saveDate: SaveDateTimeFun = (date: string): void => CaptureTools.addOrUpdateFeedbackById(this._store, capturedLocation, paneId,
            {
                category : this._settings.Analytics.Feedback.category,
                action : this._settings.Analytics.Feedback.actions.completePane,
                label : { Date: date },
                value : paneId,
            });
            let saveTime;
            if(metadata.disableTimePicker === "false"){
                saveTime = (time: string): void => CaptureTools.addOrUpdateFeedbackById(this._store, capturedLocation, paneId,
                    {
                        category : this._settings.Analytics.Feedback.category,
                        action : this._settings.Analytics.Feedback.actions.completePane,
                        label : { Time: time },
                        value : paneId,
                    });
            }


            this._calendarWidget.configure(metadata, saveDate, saveTime);

            const attributes = {};
            attributes['data-id'] = paneId;
            attributes['data-active'] = active;

            parameters['attributes'] = attributes;

            const closeIconNode = this.buildCloseIcon(closeIcon, metadata, paneId);
            const headerNode = this.buildHeader(header);
            const bodyNodes = this.buildBody(prompt, metadata, paneId);

            childNodes = childNodes.concat(closeIconNode, headerNode, bodyNodes);

            return h('div', parameters, childNodes);
        };
    }

    //#endregion



    //#region Builder Methods

    buildBody(prompt: string, metadata: MetaData, paneId: string): VNode
    {
        const promptElement = h('div', { className: this._settings.classNames.feedbackPrompt}, prompt);
        const calendarWidget = this._calendarWidget.render();
        const buttonElement = this.buildButtonElement(metadata, paneId);
        const children = [promptElement, calendarWidget ?? "", buttonElement];

        return h('div', { className : this._settings.classNames.feedbackBody }, children);
    }

    buildCloseIcon(closeIcon: boolean, metadata: MetaData, paneId: string): VNode | []
    {
        const properties =
        {
            ariaLabel: this._settings.accessibility.aria.feedbackOverlayCloseIcon,
            className: this._settings.classNames.dataCollectionCloseButton,
            onclick: (): void => { this.endPane(false, metadata, paneId); },
        };

        return closeIcon ? h('button', properties, []) : [];
    }

    buildHeader(headerText: string): VNode
    {
        return h('div', { className: this._settings.classNames.feedbackHeader }, headerText);
    }

    buildButtonElement(metadata: MetaData, paneId: string): VNode
    {
        let submitProperties: Record<string, unknown> = {};
        submitProperties =
        {
            className: this._settings.classNames.dataCollectionSubmitButton,
            onclick: (): void => { this.endPane(true, metadata, paneId); },
        };

        const cancelProperties: Record<string, unknown> =
        {
            className: this._settings.classNames.dataCollectionCancelButton,
            onclick: (): void => { this.endPane(false, metadata, paneId); },
        };

        const feedback = this._store?.getState()
            [this._settings.stateLocations.History.Base]
            [this._settings.stateLocations.History.capturedFeedback] ||
            [];
        const pane = feedback.find((item: { paneId: string }) => item.paneId === paneId);
        if (!pane ||
            !pane.value.label.Date ||
            (!pane.value.label.Time && metadata.disableTimePicker === "false"))
        {
          submitProperties.disabled = true;
        }

        const submitButton = h('button', submitProperties, metadata.buttonText);
        const cancelButton = h('button', cancelProperties, metadata.cancelText);

        return h('div', { className: this._settings.classNames.dataCollectionButtonArea }, [submitButton, cancelButton]);
    }

    //#endgregion



    //#region Action Methods

    endPane(isSubmission: boolean, metadata: MetaData, paneId: string): void
    {
        const panesLocation = this._settings.stateLocations.History.feedbackPanes;
        const activeLocation = this._settings.stateLocations.History.displayFeedback;
        const capturedLocation = this._settings.stateLocations.History.capturedFeedback;
        const capturedStageLocation = this._settings.stateLocations.History.capturedStageLocation;
        const action = isSubmission ?
            this._settings.Analytics.Feedback.actions.completeFeedback :
            this._settings.Analytics.Feedback.actions.abortFeedback;

        if (!isSubmission)
        {
            CaptureTools.addOrUpdateFeedbackById(this._store, capturedLocation, paneId,
            {
                category: this._settings.Analytics.Feedback.category,
                action: this._settings.Analytics.Feedback.actions.abortPane,
                label: { Date: "", Time: "" },
                value: paneId,
            });
        }

        if (metadata.nextPane === this._settings.ResponseActions.displayFeedbackPanes.nextPaneEndKey)
        {
            this._store?.dispatch(Actions.endDisplayFeedbackPanes(panesLocation, activeLocation));

            setTimeout(() => CaptureTools.endFeedback(this._store, capturedLocation, capturedStageLocation, paneId,
            {
                category: this._settings.Analytics.Feedback.category,
                action: action,
            }), 0);
        }
        else
        {
            this._store?.dispatch(Actions.setActiveFeedbackPane(metadata.nextPane, panesLocation));
        }

        setTimeout(() => this._calendarWidget.clearConfig(), 0);
    }

    //#endregion



    //#region Helper Methods

    doesRender(message: CalendarWidgetConfig): boolean
    {
        return message.type === this._settings.ResponseActions.displayFeedbackPanes.paneTypes.calendarWidget;
    }

    //#endregion
}


// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/window-feedback-overlay/src/renderers/calendarWidgetPane.ts